import { Fragment, useCallback, useMemo, useState } from 'react';
import {
    DndContext,
    type DragEndEvent,
    type DragStartEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import cn from 'classnames';
import get from 'lodash/get';
import { getCollisionDetection } from './lib/collisionDetection';
import { sortItemsAfterDrop } from './lib/sortItemsAfterDrop';
import { type TreeRow } from '../../lib/types';
import { DraggableRow } from '../DraggableRow';
import { DraggingRowsOverlay } from '../DraggingRowsOverlay';
import { DroppableSlot } from '../DroppableSlot';
import { RowDivider } from '../RowDivider';
import { type TreeEditorProps } from './types';
import styles from './TreeEditor.module.css';

interface TreeEditorContentProps<Attributes> extends TreeEditorProps<Attributes> {
    flatNodes: TreeRow<Attributes>[];
    items: TreeRow<Attributes>[];
    paddingTop?: number;
    paddingBottom?: number;
    useVirtualSpacers?: boolean;
    onDragStartEffect?: () => void;
    onDragEndEffect?: () => void;
}

export function TreeEditorContent<Attributes>({
    'data-testid': dataTestId = 'TreeEditor',
    size = 'm',
    onUpdateItem,
    onChange,
    onDragging,
    renderNode,
    IconDnd,
    iconSize,
    isVirtualized = false,
    disableDragAndDrop = false,
    settings,
    flatNodes,
    items,
    paddingTop = 0,
    paddingBottom = 0,
    useVirtualSpacers = false,
    onDragStartEffect,
    onDragEndEffect,
}: TreeEditorContentProps<Attributes>) {
    const sensors = useSensors(useSensor(PointerSensor));
    const [activeId, setActiveId] = useState<string | null>(null);
    const activeNode = useMemo(() => flatNodes.find((node) => node.id === activeId), [activeId, flatNodes]);

    const handleDragStart = useCallback(
        ({ active }: DragStartEvent) => {
            setActiveId(String(active.id));
            onDragStartEffect?.();
            onDragging?.(true);
        },
        [onDragStartEffect, onDragging]
    );

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            setActiveId(null);
            onDragEndEffect?.();

            const newSortedItems = sortItemsAfterDrop(flatNodes, onUpdateItem, event);

            if (newSortedItems && newSortedItems.length === flatNodes.length) {
                onChange(newSortedItems);
            }

            onDragging?.(false);
        },
        [flatNodes, onChange, onDragEndEffect, onDragging, onUpdateItem]
    );

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
        onDragEndEffect?.();
        onDragging?.(false);
    }, [onDragEndEffect, onDragging]);

    const renderVirtualSpacer = (height: number) =>
        useVirtualSpacers && height > 0 ? <div style={{ height: `${height}px` }} /> : null;

    const renderTreeContent = () => {
        if (disableDragAndDrop) {
            return (
                <>
                    {renderVirtualSpacer(paddingTop)}
                    {items.map((node) => (
                        <Fragment key={node.id}>
                            {settings?.onDividerClick && (
                                <RowDivider
                                    newDisplaySequence={get(node.attributes, 'display_sequence') - 0.01}
                                    onClick={settings.onDividerClick}
                                    tooltipText={settings.dividerTooltipText}
                                />
                            )}
                            <div
                                className={cn(styles.row, settings?.itemStyles)}
                                style={{ paddingLeft: node.level * 32 }}
                            >
                                {renderNode({
                                    size,
                                    node,
                                    'data-testid': `${dataTestId}.Item`,
                                    isSelected: false,
                                    isOpen: true,
                                    isOpenDisabled: true,
                                })}
                            </div>
                        </Fragment>
                    ))}
                    {renderVirtualSpacer(paddingBottom)}
                </>
            );
        }

        return (
            <>
                {renderVirtualSpacer(paddingTop)}
                {items.map((node, index) => (
                    <Fragment key={node.id}>
                        {settings?.onDividerClick && (
                            <RowDivider
                                newDisplaySequence={get(node.attributes, 'display_sequence') - 0.01}
                                onClick={settings.onDividerClick}
                                tooltipText={settings.dividerTooltipText}
                            />
                        )}
                        <DraggableRow
                            node={node}
                            index={index}
                            size={size}
                            renderNode={renderNode}
                            IconDnd={IconDnd}
                            iconSize={iconSize}
                            settings={settings}
                        />
                    </Fragment>
                ))}
                {isVirtualized && activeNode && !items.some((item) => item.id === activeNode.id) && (
                    <DraggableRow
                        node={activeNode}
                        index={activeNode.index}
                        size={size}
                        renderNode={renderNode}
                        IconDnd={IconDnd}
                        iconSize={iconSize}
                        settings={settings}
                    />
                )}
                {renderVirtualSpacer(paddingBottom)}
                <DroppableSlot isDroppable size={size} />
                {activeNode && (
                    <DraggingRowsOverlay
                        activeNode={activeNode}
                        settings={settings}
                        size={size}
                        renderNode={renderNode}
                        IconDnd={IconDnd}
                        iconSize={iconSize}
                    />
                )}
            </>
        );
    };

    if (disableDragAndDrop) {
        return renderTreeContent();
    }

    return (
        <DndContext
            autoScroll
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            collisionDetection={getCollisionDetection(settings?.withLevels)}
        >
            {renderTreeContent()}
        </DndContext>
    );
}
